
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Order} from './Order';
import {FieldModelOrder} from './Order';
import {OrderState} from './OrderState';
import {FieldModelOrderState} from './OrderState';
import {Account} from './Account';
import {FieldModelAccount} from './Account';
import {AccountMargin} from './AccountMargin';
import {FieldModelAccountMargin} from './AccountMargin';

/**
 * OrderWithAccount struct
 */
class OrderWithAccount {
  /**
   * Initialize struct
   * @param {!Order=} order
   * @param {OrderState=} orderState
   * @param {Account=} account
   * @param {AccountMargin=} accountMargin
   * @constructor
   */
  constructor (order = new Order(), orderState = null, account = null, accountMargin = null) {
    this.Order = order
    this.OrderState = orderState
    this.Account = account
    this.AccountMargin = accountMargin
  }

  /**
   * Copy struct (shallow copy)
   * @this {!OrderWithAccount}
   * @param {!OrderWithAccount} other Other struct
   * @returns {!OrderWithAccount} This struct
   */
  copy (other) {
    if (other.Order != null) {
      this.Order = Order.fromObject(other.Order)
    } else {
      this.Order = null
    }
    if (other.OrderState != null) {
      this.OrderState = OrderState.fromObject(other.OrderState)
    } else {
      this.OrderState = null
    }
    if (other.Account != null) {
      this.Account = Account.fromObject(other.Account)
    } else {
      this.Account = null
    }
    if (other.AccountMargin != null) {
      this.AccountMargin = AccountMargin.fromObject(other.AccountMargin)
    } else {
      this.AccountMargin = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!OrderWithAccount}
   * @returns {!OrderWithAccount} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new OrderWithAccountModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new OrderWithAccountModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!OrderWithAccount}
   * @param {!OrderWithAccount} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof OrderWithAccount)) {
      throw new TypeError('Instance of OrderWithAccount is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!OrderWithAccount}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Order: ((this.Order != null) ? this.Order : null),
      OrderState: ((this.OrderState != null) ? this.OrderState : null),
      Account: ((this.Account != null) ? this.Account : null),
      AccountMargin: ((this.AccountMargin != null) ? this.AccountMargin : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return OrderWithAccount.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!OrderWithAccount} other Object value
   * @returns {!OrderWithAccount} Created struct
   */
  static fromObject (other) {
    return new OrderWithAccount().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!OrderWithAccount}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return OrderWithAccount.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!OrderWithAccount}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 181
  }
}

export { OrderWithAccount };

/**
 * Fast Binary Encoding OrderWithAccount field model
 */
class FieldModelOrderWithAccount extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Order = new FieldModelOrder(buffer, 4 + 4)
    this.OrderState = new fbe.FieldModelOptional(new FieldModelOrderState(buffer, this.Order.fbeOffset + this.Order.fbeSize), buffer, this.Order.fbeOffset + this.Order.fbeSize)
    this.Account = new fbe.FieldModelOptional(new FieldModelAccount(buffer, this.OrderState.fbeOffset + this.OrderState.fbeSize), buffer, this.OrderState.fbeOffset + this.OrderState.fbeSize)
    this.AccountMargin = new fbe.FieldModelOptional(new FieldModelAccountMargin(buffer, this.Account.fbeOffset + this.Account.fbeSize), buffer, this.Account.fbeOffset + this.Account.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelOrderWithAccount}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelOrderWithAccount}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Order.fbeSize + this.OrderState.fbeSize + this.Account.fbeSize + this.AccountMargin.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelOrderWithAccount}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Order.fbeExtra + this.OrderState.fbeExtra + this.Account.fbeExtra + this.AccountMargin.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelOrderWithAccount}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelOrderWithAccount.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelOrderWithAccount}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 181
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelOrderWithAccount}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelOrderWithAccount}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Order.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Order.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Order.fbeSize

    if ((fbeCurrentSize + this.OrderState.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrderState.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderState.fbeSize

    if ((fbeCurrentSize + this.Account.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Account.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Account.fbeSize

    if ((fbeCurrentSize + this.AccountMargin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountMargin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountMargin.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelOrderWithAccount}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelOrderWithAccount}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelOrderWithAccount}
   * @param {!OrderWithAccount} fbeValue Default value, defaults is new OrderWithAccount()
   * @returns {!OrderWithAccount} OrderWithAccount value
   */
  get (fbeValue = new OrderWithAccount()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelOrderWithAccount}
   * @param {!OrderWithAccount} fbeValue OrderWithAccount value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Order.fbeSize) <= fbeStructSize) {
      fbeValue.Order = this.Order.get()
    } else {
      fbeValue.Order = new Order()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Order.fbeSize

    if ((fbeCurrentSize + this.OrderState.fbeSize) <= fbeStructSize) {
      fbeValue.OrderState = this.OrderState.get()
    } else {
      fbeValue.OrderState = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderState.fbeSize

    if ((fbeCurrentSize + this.Account.fbeSize) <= fbeStructSize) {
      fbeValue.Account = this.Account.get()
    } else {
      fbeValue.Account = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Account.fbeSize

    if ((fbeCurrentSize + this.AccountMargin.fbeSize) <= fbeStructSize) {
      fbeValue.AccountMargin = this.AccountMargin.get()
    } else {
      fbeValue.AccountMargin = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountMargin.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelOrderWithAccount}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelOrderWithAccount}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelOrderWithAccount}
   * @param {!OrderWithAccount} fbeValue OrderWithAccount value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelOrderWithAccount}
   * @param {OrderWithAccount} fbeValue OrderWithAccount value
   */
  setFields (fbeValue) {
    this.Order.set(fbeValue.Order)
    this.OrderState.set(fbeValue.OrderState)
    this.Account.set(fbeValue.Account)
    this.AccountMargin.set(fbeValue.AccountMargin)
  }
}

export { FieldModelOrderWithAccount };

/**
 * Fast Binary Encoding OrderWithAccount model
 */
class OrderWithAccountModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelOrderWithAccount(this.buffer, 4)
  }

  /**
   * Get the OrderWithAccount model
   * @this {!OrderWithAccountModel}
   * @returns {!FieldModelOrderWithAccount} model OrderWithAccount model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!OrderWithAccountModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!OrderWithAccountModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return OrderWithAccountModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!OrderWithAccountModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelOrderWithAccount.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!OrderWithAccountModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!OrderWithAccountModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!OrderWithAccountModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!OrderWithAccountModel}
   * @param {!OrderWithAccount} value OrderWithAccount value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!OrderWithAccountModel}
   * @param {!OrderWithAccount} value OrderWithAccount value, defaults is new OrderWithAccount()
   * @return {!object} Deserialized OrderWithAccount value and its size
   */
  deserialize (value = new OrderWithAccount()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new OrderWithAccount(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new OrderWithAccount(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!OrderWithAccountModel}
   * @param {!number} prev Previous OrderWithAccount model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { OrderWithAccountModel };
